import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export type XnInputType = 'text' | 'email' | 'select' | 'date' | 'row' | 'password' | 'checkbox' | 'phone' | 'empty' | 'file' | 'line' | 'time' | 'switch';

export type XnFormControlValidationType = 'postcode' | 'address';

export interface XnFieldStateEvent{
  name: string,
  valid: boolean,
  errors?: string[]
}

export interface XnOnChangedEvent{
  value?: any,
  name: string,
  values?: any,
  target?: any
}

export type CalendarView = 'month' | 'date' | 'year'

interface PasswordRule{
  status?: boolean,
  message?: string
}

@Component({
  selector: 'app-xn-form-control',
  standalone: false,
  templateUrl: './xn-form-control.component.html',
  styleUrls: ['./xn-form-control.component.sass'],
  providers: [
    { 
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => XnFormControlComponent),
      multi: true
    }
  ]
})
export class XnFormControlComponent implements ControlValueAccessor {

  fieldName = ""
  @Input('label') label?: string
  @Input('name') name?: string
  @Input('type') type: XnInputType = 'text';
  @Input('required') required?: boolean = false;
  @Input('submitted') submitted?: boolean = false;
  @Input('styleClass') styleClass: string = '';
  @Input('displayType') displayType: 'normal' | 'float' = 'normal';
  @Input('placeholder') placeholder?: string
  @Input('customValidateError') customValidateError?: (value: any, data?: any) => string
  @Input('values') values: any
  @Output('onFieldChanged') onFieldChanged = new EventEmitter<XnOnChangedEvent>();
  @Input('autocomplete') autocomplete?: string
  @Input('readonly') readonly?: boolean = false;
  @Input() disabled?: boolean = false;

  @Input('selectOptions') selectOptions?: any[] = [];
  @Input('optionLabel') optionLabel?: string
  @Input('autoDisplayFirst') autoDisplayFirst: boolean = false
  @Input('optionValue') optionValue?: string
  @Input('translateOptions') translateOptions?: boolean = false; 

  @Input('dateFormat') dateFormat?: string
  @Input('minDate') minDate?: Date
  @Input('maxDate') maxDate?: Date
  @Input('calendarView') calendarView?: CalendarView;

  @Input('passwordToggleMask') passwordToggleMask?: boolean = false
  @Input('passwordFeedback') passwordFeedback?: boolean = false
  @Input('validationType') validationType?: XnFormControlValidationType;
  @Input('hidePasswordHints') hidePasswordHints?: boolean = false;

  @Input() fileHint? = "";
  @Input() fileHintHtml? = "";
  @Input() fileHintHtmlTranslate? = "";
  @Input() fileButtonText? = "";
  @Input() fileAccept? = ""
  @Input() fileMaxSizeInMb?: number = 0;

  @Input() hideLabel?: boolean = false;

  @Input('minLength') minLength?: number
  @Input('maxLength') maxLength?: number

  // state change from valid to invalid or from invalid to valid
  @Output('onStateChanged') onStateChanged = new EventEmitter<XnFieldStateEvent>();
  private _validState?: boolean = undefined;

  _value: any = "";
  private _errors: string[] = []
  private _touched = false;

  @ViewChild('formControl', {read: NgModel}) formControl?: NgModel;
  @ViewChild('fileInput') fileInput?: ElementRef;

  passwordRules: PasswordRule[] = [{
    message: 'password.rules.min-characters',
  }, {
    message: 'password.rules.at-least-1-lower-case-letter'
  }, {
    message: 'password.rules.at-least-1-upper-case-letter'
  }, {
    message: 'password.rules.at-least-1-number'
  }, {
    message: 'password.rules.at-least-1-special-letter'
  }];

  constructor(
    private translate: TranslateService,
  ) { }

  writeValue(value: any): void {
    if(value !== undefined){
      this._value = value;
    }
  }

  propagateChange = (_: any) => {

  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    
  }

  setDisabledState?(isDisabled: boolean): void {
    
  }

  ngOnInit(): void {
  }

  clearError(){
    this._errors = [];
  }

  addError(error: string){
    this._errors.push(error);
    this.validState = false;
  }

  resetPasswordErrors(){
    this.passwordRules.forEach(e => {
      e.status = false;
    })
  }

  validatePassword(value: string){
    this.passwordRules.forEach(e => {
      e.status = true;
    });

    let result = true;
    if(!value) {
      this.resetPasswordErrors();
      return false;
    }
    if(value.length < 8) {
      result = false;
      this.passwordRules[0].status = false;
    }
    if(value.length > 500) {
      result = false;
    }

    if(!/[a-z]/.test(value)) {
      result = false;
      this.passwordRules[1].status = false;
    }
    if(!/[A-Z]/.test(value)){
      result = false;
      this.passwordRules[2].status = false;
    }
    if(!/\d/.test(value)) {
      result = false;
      this.passwordRules[3].status = false;
    }

    if(!/[@\.\[\]\(\)\!#\$%\^\&\*\/]/.test(value)) {
      this.passwordRules[4].status = false;
      result = false;
    }
    return result;
  }

  // validatePassword(value: string){
  //   if(!value) return false;
  //   if(value.length < 6) return false;
  //   return true;
  // }

  validatePostCode(value: string){
    if(!value) return false;
    if(/^[a-z 0-9]+$/i.test(value)){
      return true;
    }
    return false;
  }

  validateAddress(value: string){
    if(!value) return false;

    if(/^[0-9\.\*\(\)\[\]\-_&\$#@\!,\? ]+$/i.test(value)){
      // only number & special character
      return false;
    }

    return true;
  }

  hasErrors(): boolean{
    this.clearError();
    if(this.required && !this._value) {
       this.addError('form.error.required-field')
       this.type == 'password' && this.resetPasswordErrors();
       return true;
    }

    if(this.type === 'email' && this._value && !this.ValidateEmail(this._value) ){
      this.addError('form.error.invalid-email')
      return true;
    }

    if(this.type === 'phone' && this._value && !this.validatePhoneNumber(this._value)){
      this.addError('form.error.invalid-phone-number');
      return true;
    }

    if(this.type === 'password' && !this.validatePassword(this._value)){
      this.addError('form.error.invalid-password');
      return true;
    }

    if(this.type === 'date' && this._value){
      const value : Date = this._value as Date;
      if(this.minDate && this.compareDates(value, this.minDate) == -1){
        this.addError('form.error.invalid-date')
        return true;
      }

      if(this.maxDate && this.compareDates(value, this.maxDate) == 1){
        this.addError('form.error.invalid-date')
        return true;
      }
    }

    if(this.type === 'file' && this._value){
      if(this._value.file){
        if(this.fileMaxSizeInMb && this.fileMaxSizeInMb > 0){
          // verify size
          const sizeInMb = this._value.file.size / 1024 / 1024;
          if(sizeInMb > this.fileMaxSizeInMb){
            // invalid file
            this.addError('form.error.file-too-big');
            return true;
          }
        }  
      }
    }

    if(this.validationType === 'postcode' && !this.validatePostCode(this._value)){
      this.addError('form.error.invalid-postcode');
      return true;
    }
    else if(this.validationType === 'address' && !this.validateAddress(this._value)){
      this.addError('form.error.invalid-address');
      return true;
    }
    
    if(typeof this._value === 'string'){
      if(this.maxLength && this._value.length > this.maxLength){
        let msg = this.translate.instant(`form.error.invalid-maximum-length`);
        msg = msg.replace(`{number}`, this.maxLength);
        this.addError(msg);
        return true;
      }

      if(this.minLength !== undefined && this._value.length < this.minLength){
        let msg = this.translate.instant(`form.error.invalid-min-length`);
        msg = msg.replace(`{number}`, this.minLength);
        this.addError(msg);
        return true;
      }
    }

    if(this.customValidateError && typeof this.customValidateError === 'function'){
      const customError = this.customValidateError(this._value, this.values) as string;
      if(customError){
        this.addError(customError);
        return true;
      }
    }

    this.validState = true;

    return false;
  }

  shouldDisplayErrors(){
    const hasError = this.hasErrors();
    if(!hasError) return false;

    if(this._value || this._touched || this.submitted){
      return true;
    }

    return false;
  }

  getError(){
    if(!this._errors || !this._errors.length) return '';
    return this._errors[0];
  }

  resetTouch(){
    this._touched = false;
    this.formControl?.control?.markAsPristine({onlySelf: true});
    if(this.type === 'file'){
      if(this.fileInput && this.fileInput.nativeElement){
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  isTouched(){
    return this._touched;
  }

  reset(){
    this.resetTouch();
  }

  _onValueChange(value: any){
    this._touched = true;
    this.propagateChange(value);
    this.onFieldChanged.emit({
      name: this.name || '',
      value: value,
      values: this.values
    });
  }

  ValidateEmail(inputText: string): boolean{
    if(!inputText) return false;
    if(!inputText.includes('@') || !inputText.includes('.')) return false;
    if(inputText.length > 255) return false;
    if(!/[a-z]+/i.test(inputText)) return false;
    if(/^[\.,\!\@\#\$\%\^\&\*\(\)\-]/i.test(inputText)) return false;
    const emailReg = /^[^\s@&\^,]+@[a-z0-9]+(\.[a-z0-9]{2,})+$/i;
    return emailReg.test(inputText);
  }

  getDisplayLabel(): string{
    return this.label || this.name || '';
  }

  set validState(state: boolean){
    if(this._validState === state) return;
    this._validState = state;
    this.onStateChanged.emit({
      name: this.name || '',
      valid: this._validState,
      errors: this._errors
    });
  }

  getPlaceholder(): string{
    return this.placeholder || '';
  }

  getFieldType(): string{
    if(this.type === 'phone') return 'tel';
    return this.type;
  }

  validatePhoneNumber(number: string): boolean{
    if(!number) return false;
    if(number.length < 5) return false;

    if(number.length > 17) return false;

    return /^(\(\+\d+\))?[\d\+ ]+$/i.test(number);
  }

  onFileChanged(event: any){
    if (event && event.target.files && event.target.files.length > 0) {
      const reader = new FileReader();
      const file = event.target.files[0];

      reader.readAsDataURL(file);
      reader.onload = () => {
        const fileContent = reader.result;
        if (fileContent != null) {
          const content = fileContent.toString().split('base64,')[1];

          this._touched = true;
          const fileData = {
            file: file,
            content: content
          };
          this._value = fileData;
          this.propagateChange(fileData);
          this.onFieldChanged.emit({
            name: this.name || '',
            value: fileData,
            values: this.values,
            target: event.target
          });
        }        
      };
    }
  }

  compareDates(date1: Date, date2: Date) {
    const day1 = date1.getDate();
    const month1 = date1.getMonth(); // 0-based index (January is 0, December is 11)
    const year1 = date1.getFullYear();

    const day2 = date2.getDate();
    const month2 = date2.getMonth();
    const year2 = date2.getFullYear();

    // Create new Date objects using only year, month, and day (time will default to midnight)
    const formattedDate1 = new Date(year1, month1, day1);
    const formattedDate2 = new Date(year2, month2, day2);

    if (formattedDate1 > formattedDate2) {
        return 1;  // date1 is greater (later)
    } else if (formattedDate1 < formattedDate2) {
        return -1; // date1 is lesser (earlier)
    } else {
        return 0;  // dates are equal
    }
  }
}
